import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const MentalHealthResources = () => {
    const { t } = useTranslation();
    const [activeCategory, setActiveCategory] = useState('emergency');

    const resources = t('resources_list.items', { returnObjects: true }) || {};
    const categories = t('resources_list.categories', { returnObjects: true }) || [];

    const renderResourceItem = (item) => {
        switch (item.type) {
            case 'phone':
                return (
                    <div className="bg-white dark:bg-stone-900 rounded-3xl p-6 sm:p-7 shadow-soft border border-stone-200/80 dark:border-stone-800 flex flex-col justify-between h-full">
                        <div>
                            <div className="flex justify-between items-start mb-3">
                                <h3 className="font-display font-bold text-stone-900 dark:text-stone-100 text-lg">{item.name}</h3>
                                <span className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 px-3 py-1 rounded-full text-xs font-semibold">
                                    {item.available}
                                </span>
                            </div>
                            <p className="text-stone-600 dark:text-stone-400 text-sm mb-6 leading-relaxed">{item.description}</p>
                        </div>
                        <div className="pt-4 border-t border-stone-100 dark:border-stone-800">
                            <a
                                href={`tel:${item.contact}`}
                                className="inline-flex items-center justify-center w-full px-5 py-3 rounded-full bg-terracotta hover:bg-terracotta-dark text-white font-semibold text-sm shadow-warm transition-all duration-200 transform hover:-translate-y-0.5"
                            >
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                </svg>
                                {item.contact}
                            </a>
                        </div>
                    </div>
                );

            case 'website':
                return (
                    <div className="bg-white dark:bg-stone-900 rounded-3xl p-6 sm:p-7 shadow-soft border border-stone-200/80 dark:border-stone-800 flex flex-col justify-between h-full">
                        <div>
                            <h3 className="font-display font-bold text-stone-900 dark:text-stone-100 text-lg mb-3">{item.name}</h3>
                            <p className="text-stone-600 dark:text-stone-400 text-sm mb-6 leading-relaxed">{item.description}</p>
                        </div>
                        <div className="pt-4 border-t border-stone-100 dark:border-stone-800">
                            <a
                                href={item.contact}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-full px-5 py-3 rounded-full bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-800 dark:text-stone-200 font-semibold text-sm transition-colors"
                            >
                                <svg className="w-4 h-4 mr-2 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                {t('resources_list.ui.visit_site')}
                            </a>
                        </div>
                    </div>
                );

            case 'book':
                return (
                    <div className="bg-white dark:bg-stone-900 rounded-3xl p-6 sm:p-7 shadow-soft border border-stone-200/80 dark:border-stone-800 flex flex-col justify-between h-full">
                        <div>
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="font-display font-bold text-stone-900 dark:text-stone-100 text-lg">{item.name}</h3>
                                    <p className="text-xs text-stone-500 dark:text-stone-400 font-medium">{t('resources_list.ui.by')} {item.author}</p>
                                </div>
                                <span className="bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 px-3 py-1 rounded-full text-xs font-semibold">
                                    {item.category}
                                </span>
                            </div>
                            <p className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed">{item.description}</p>
                        </div>
                    </div>
                );

            case 'app':
                return (
                    <div className="bg-white dark:bg-stone-900 rounded-3xl p-6 sm:p-7 shadow-soft border border-stone-200/80 dark:border-stone-800 flex flex-col justify-between h-full">
                        <div>
                            <div className="flex justify-between items-start mb-3">
                                <h3 className="font-display font-bold text-stone-900 dark:text-stone-100 text-lg">{item.name}</h3>
                                <span className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 px-3 py-1 rounded-full text-xs font-semibold">
                                    {item.platform}
                                </span>
                            </div>
                            <p className="text-stone-600 dark:text-stone-400 text-sm mb-4 leading-relaxed">{item.description}</p>
                            <div className="space-y-1.5 pt-2">
                                {item.features && item.features.map((feature, index) => (
                                    <div key={index} className="flex items-center text-xs text-stone-500 dark:text-stone-400">
                                        <svg className="w-3.5 h-3.5 mr-2 text-terracotta flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                        </svg>
                                        {feature}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'exercise':
                return (
                    <div className="bg-white dark:bg-stone-900 rounded-3xl p-6 sm:p-7 shadow-soft border border-stone-200/80 dark:border-stone-800 flex flex-col justify-between h-full">
                        <div>
                            <div className="flex justify-between items-start mb-3">
                                <h3 className="font-display font-bold text-stone-900 dark:text-stone-100 text-lg">{item.name}</h3>
                                <span className="bg-terracotta-100 text-terracotta-800 dark:bg-stone-800 dark:text-terracotta-light px-3 py-1 rounded-full text-xs font-semibold">
                                    {item.duration}
                                </span>
                            </div>
                            <p className="text-stone-600 dark:text-stone-400 text-sm mb-4 leading-relaxed">{item.description}</p>
                            <div className="space-y-2 pt-2">
                                <h4 className="text-xs font-bold text-stone-800 dark:text-stone-200 uppercase tracking-wider">{t('resources_list.ui.steps')}</h4>
                                <ol className="space-y-2">
                                    {item.steps && item.steps.map((step, index) => (
                                        <li key={index} className="flex items-start text-xs sm:text-sm text-stone-600 dark:text-stone-400">
                                            <span className="bg-terracotta text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2.5 mt-0.5 flex-shrink-0 font-bold">
                                                {index + 1}
                                            </span>
                                            <span>{step}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const currentCategoryData = resources[activeCategory];

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center max-w-2xl mx-auto mb-14">
                <span className="text-xs font-semibold uppercase tracking-wider text-terracotta mb-2 block">
                    Ghiduri Practice
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                    Resurse de Sănătate Mentală
                </h2>
                <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg">
                    Selecție de cărți, aplicații validate, exerciții și contacte de urgență.
                </p>
            </div>

            <div className="max-w-5xl mx-auto">
                {/* Category Pills */}
                {Array.isArray(categories) && categories.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-12">
                        {categories.map(category => (
                            <button
                                key={category.id}
                                onClick={() => setActiveCategory(category.id)}
                                className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                                    activeCategory === category.id
                                        ? 'bg-terracotta text-white shadow-warm'
                                        : 'bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 border border-stone-200 dark:border-stone-800'
                                }`}
                            >
                                <span className="mr-2">{category.icon}</span>
                                {category.name}
                            </button>
                        ))}
                    </div>
                )}

                {/* Resource Content Grid */}
                {currentCategoryData && currentCategoryData.items && (
                    <div className="mb-12">
                        <h3 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-8 text-center flex items-center justify-center gap-3">
                            <span>{currentCategoryData.icon}</span>
                            <span>{currentCategoryData.title}</span>
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {currentCategoryData.items.map((item, index) => (
                                <div key={index}>
                                    {renderResourceItem(item)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Emergency Disclaimer */}
                <div className="bg-amber-50/70 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-3xl p-6 sm:p-8 text-center">
                    <div className="font-bold text-amber-900 dark:text-amber-200 text-base mb-2">
                        {t('resources_list.ui.important_title')}
                    </div>
                    <p className="text-amber-800/90 dark:text-amber-300/90 text-xs sm:text-sm leading-relaxed max-w-2xl mx-auto">
                        {t('resources_list.ui.important_desc')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MentalHealthResources;
